import { PrzyciskZWyborem } from '@/components/PrzyciskZWyborem';
import { zlozMapePdf } from '@/lib/planogram-mapa-pdf';
import { MAX_SPAN_RYSOWANY, type TrybKoloru } from '@/lib/planogram-mapa';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Minimize2, Maximize2, Columns3, Download, FileSpreadsheet, FileText, Info, LayoutGrid, Link2, Loader2, Search, TriangleAlert, X, Repeat2 } from 'lucide-react';
import { Fragment, Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';
import { Beben3D } from '@/components/Beben3D';
import { PasekEdycjiKolumn, type WybranaZmiana } from '@/components/EdycjaKolumn';
import { GranicaBledu } from '@/components/GranicaBledu';
import { PanelEdycji } from '@/components/PanelEdycji';
import { PodmianaIndeksu } from '@/components/PodmianaIndeksu';
import { PasekNaprawy } from '@/components/PasekNaprawy';
import { PasekZaznaczenia } from '@/components/PasekZaznaczenia';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import {
  buildCsvBlob,
  buildPdfBlob,
  buildXlsxBlob,
  downloadBlob,
  type ExportColumn,
  type ExportRow,
} from '@/lib/export';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { listMachines } from '@/lib/machines-api';
import {
  fetchKolumnyBebna,
  fetchPlanogram,
  type Compartment,
  type Planogram as PlanogramData,
  type Propozycja,
} from '@/lib/planogram-api';
import { STOCK_STEPS, stanSzukania, stockStep } from '@/lib/planogram-kolory';
import { cn } from '@/lib/utils';

/**
 * ⚠️ BYŁY TRZY TRYBY, SĄ DWA. `fill` i `stock` liczyły DOKŁADNIE TO SAMO — procent zapełnienia —
 * i różniły się wyłącznie paletą: `fill` skalą sekwencyjną jednego odcienia, `stock` paletą stanów
 * z ikonami. Dwa wejścia do menu dla jednej informacji, a czytelniejsze było drugie. Zostaje
 * paleta stanów, pod nazwą „Zapełnienie"; skala sekwencyjna znika razem z `SEQ_STEPS`.
 */
type ColorMode = 'stock' | 'groups';

/** Tylko TRZY gniazda kategorii — przy siatce porównuje się dowolne dwie komórki. */
const CAT_SLOTS = [
  { bg: 'var(--pg-cat-1)', ink: 'var(--pg-cat-1-ink)' },
  { bg: 'var(--pg-cat-2)', ink: 'var(--pg-cat-2-ink)' },
  { bg: 'var(--pg-cat-3)', ink: 'var(--pg-cat-3-ink)' },
] as const;

const NEUTRAL = { bg: 'var(--pg-other)', ink: 'var(--pg-other-ink)' };

/**
 * BĘBEN 3D W WERSJI DRUGIEJ — ładowany dopiero przy wejściu na zakładkę.
 *
 * ⚠️ `lazy` NIE JEST TU OZDOBNIKIEM. Ten widok ciągnie `three` (~150 kB po kompresji); wciągnięty
 * z głównym pakietem kosztowałby każde wejście do portalu, także tych, którzy planogramu nigdy
 * nie otwierają. Osobny kawałek zjeżdża wyłącznie temu, kto kliknie „3D_V2".
 */
const Beben3DV2 = lazy(() => import('@/components/Beben3DV2').then((m) => ({ default: m.Beben3DV2 })));

// ─────────────────────────────── siatka ──────────────────────────────────────

/**
 * ⚠️ ORIENTACJA SIATKI — czytaj, zanim tu cokolwiek przestawisz.
 *
 * KOLUMNY siatki to `drawers` z API (SPS `DRAWERNUMBER`), a WIERSZE to przegródki
 * (SPS `COMPARTMENTNUMBER`). Nazwy SPS są odwrotne do fizycznych: „szuflada" SPS-owa jest
 * pionowym wycinkiem bębna, a nie poziomą półką. Automat ma standardowo 36 kolumn i 13 wierszy.
 *
 * Poprzednia wersja rysowała to TRANSPONOWANE — 34 wiersze po 13 komórek — czyli planogram
 * nie odpowiadał temu, co operator widzi, otwierając automat. Etykiety mówią więc
 * „kolumna"/„wiersz" (język operatora), a w szczegółach pokazujemy współrzędne SPS,
 * żeby dało się o komórce rozmawiać przez telefon.
 *
 * Kolumna POSZERZONA blachą zajmuje 2 albo 3 miejsca bębna i wtedy numeracja przeskakuje
 * (kolumna 1 ×2 → następna jest 3). Szerokość idzie z API jako `span`.
 */

/** Szerokość jednego MIEJSCA bębna i wysokość wiersza — siatka 36×13 jedzie w poziomie. */
const SLOT_W = 104;
const ROW_H = 44;
const LABEL_W = 56;
/**
 * Poszerzenie bywa ×2 i ×3; szerszego NIE rysujemy w skali, bo to raczej dziura w danych.
 *
 * ⚠️ STAŁA JEST WSPÓLNA Z MAPĄ PDF (`lib/planogram-mapa.ts`). Dwie własne liczby znaczyłyby, że
 * wydruk pokazuje inną szafę niż siatka, na którą operator patrzy przy tym samym automacie.
 */
const MAX_VISUAL_SPAN = MAX_SPAN_RYSOWANY;

function Cell({
  c,
  drawerNo,
  rowNo,
  span,
  colorFor,
  onHover,
  onSelect,
  selected,
  szukany,
  ginie = false,
}: {
  /** `null` = w tej kolumnie NIE MA takiej przegródki (inny stan niż „pusta"). */
  c: Compartment | null;
  drawerNo: number;
  rowNo: number;
  span: number;
  colorFor: (c: Compartment) => { bg: string; ink: string };
  onHover: (c: Compartment | null, el: HTMLElement | null) => void;
  onSelect: (c: Compartment, mod: { shift: boolean; toggle: boolean }) => void;
  selected: boolean;
  /** Szukany towar — `null` = nikt nie szuka. Patrz `stanSzukania`. */
  szukany: string | null;
  /**
   * ⚠️⚠️ TA PRZEGRODA ZNIKNIE PO POSZERZENIU SĄSIADKI. Obwódka, a nie zamalowanie: towar w niej
   * ma być DALEJ WIDOCZNY, bo to on rozstrzyga, czy wolno ją poświęcić. Zakrycie go kolorem
   * zabrałoby dokładnie tę informację, dla której ostrzeżenie stoi na siatce, a nie w okienku.
   */
  ginie?: boolean;
}) {
  const { t } = useI18n();
  const wspolrzedne = `${t('planogram.column')} ${drawerNo}, ${t('planogram.row')} ${rowNo}`;

  /**
   * Trzy RÓŻNE stany, nie dwa. „Nie ma przegródki" to informacja o geometrii bębna,
   * a „przegródka pusta" o zapasie — zlanie ich w jedno kłamałoby o tym, co da się zapełnić.
   */
  if (c === null) {
    return (
      <div
        aria-label={`${wspolrzedne}: ${t('planogram.noCompartment')}`}
        className={cn('rounded-[4px] border border-dotted opacity-40', ginie && 'border-solid opacity-100')}
        style={{
          gridColumn: `span ${span}`,
          height: ROW_H,
          borderColor: ginie ? 'var(--destructive)' : 'var(--pg-none-border)',
        }}
      />
    );
  }

  const empty = c.item === null;
  const { bg, ink } = colorFor(c);
  const stan = stanSzukania(c, szukany);

  return (
    <button
      type="button"
      onMouseEnter={(e) => onHover(c, e.currentTarget)}
      onMouseLeave={() => onHover(null, null)}
      onFocus={(e) => onHover(c, e.currentTarget)}
      onBlur={() => onHover(null, null)}
      /**
       * Modyfikatory czytamy ZE ZDARZENIA, a nie ze stanu klawiatury trzymanego obok — stan
       * potrafi się rozjechać, gdy klawisz puszczono nad innym oknem. `metaKey` obok `ctrlKey`,
       * bo na macOS to Cmd jest klawiszem od zaznaczania pojedynczego.
       */
      onClick={(e) => onSelect(c, { shift: e.shiftKey, toggle: e.ctrlKey || e.metaKey })}
      aria-label={
        empty
          ? `${wspolrzedne}: ${t('planogram.emptySlot')}`
          : `${wspolrzedne}: ${c.item!.name}, ${c.qtyCurrent}/${c.qtyMax}${
              c.fillPercent === null ? '' : ` (${c.fillPercent}%)`
            }`
      }
      className={cn(
        'flex flex-col justify-center overflow-hidden rounded-[4px] px-1.5 py-1 text-left leading-tight transition-shadow',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        selected && 'ring-2 ring-ring ring-offset-1 ring-offset-background',
        empty && 'border border-dashed',
        /*
          ⚠️ SZUKANIE TO OSOBNY KANAŁ OD ZAZNACZENIA, i taka była prośba: „podświetlają się, ale
          NIE zaznaczają". Zaznaczenie zmienia to, co zrobi zapis, więc obu sensów nie wolno
          pokazać tym samym znakiem. Zaznaczenie ma obwódkę `ring`, szukanie — przygaszenie
          całej reszty, czyli znak, którego nie da się pomylić z „wybrane do zmiany".
        */
        stan === 'obok' && 'opacity-25',
        stan === 'trafienie' && 'z-10 outline outline-2 outline-offset-[-2px] outline-[var(--pg-znaleziony)]',
        /**
         * ⚠️⚠️ OBWÓDKA, NIE ZAMALOWANIE. Towar w ginącej przegrodzie ma być DALEJ WIDOCZNY —
         * to on rozstrzyga, czy wolno ją poświęcić. Zakrycie go kolorem zabrałoby dokładnie tę
         * informację, dla której ostrzeżenie stoi na siatce, a nie w osobnym oknie.
         */
        ginie && 'z-10 outline outline-2 outline-offset-[-2px] outline-[var(--destructive)]',
      )}
      /*
        ⚠️ ZAZNACZENIE MUSI BYĆ WIDAĆ TAKŻE W ŚRODKU KOMÓRKI, nie tylko po obwódce — zgłoszone
        jako „dość słabo odznaczone". Sama `ring` ginie: komórki stoją gęsto obok siebie, więc
        obwódka jednej sąsiaduje z obwódką drugiej i przy zaznaczeniu prostokąta widać siatkę
        kresek zamiast wypełnionego obszaru. Przyciemnienie wypełnienia czyta się od razu jako
        blok, a że jedzie `filter`em, zachowuje pod spodem rozróżnienie kolorów stanu.
      */
      style={{
        gridColumn: `span ${span}`,
        height: ROW_H,
        background: empty ? (selected ? 'var(--pg-other)' : 'transparent') : bg,
        color: empty ? 'var(--pg-other-ink)' : ink,
        borderColor: empty ? 'var(--pg-none-border)' : undefined,
        // pusta komórka nie ma czego przyciemniać — jej dajemy wypełnienie wyżej
        ...(selected && !empty ? { filter: 'brightness(0.62) saturate(1.15)' } : {}),
      }}
    >
      {empty ? (
        <span className="truncate text-[10px] opacity-70">{t('planogram.emptySlot')}</span>
      ) : (
        <>
          {/*
            NAZWA TOWARU w komórce, nie sama liczba. Planogram służy do pytania „w której
            kolumnie leżą rękawice nitrylowe" — przy samej liczbie sztuk trzeba było najechać
            na każdą komórkę po kolei. Dwie linie i przycięcie; pełna nazwa jest w podpowiedzi.
          */}
          <span className="line-clamp-2 text-[10px] font-medium">{c.item!.name}</span>
          {/* Wartość NIGDY nie jedzie samym kolorem — liczba stoi w komórce. */}
          <span className="text-[10px] tabular-nums opacity-80">
            {c.qtyCurrent}/{c.qtyMax}
          </span>
        </>
      )}
    </button>
  );
}

// ─────────────────────────────── strona ──────────────────────────────────────

export function Planogram() {
  const { t } = useI18n();
  const toast = useToast();
  const { groupId, island } = useIslands();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setMode] = useState<ColorMode>('stock');
  /**
   * SIATKA CZY WALEC. Siatka jest rozwinięciem bębna na płasko i lepiej się w niej czyta numery;
   * walec pokazuje to, czego siatka nie umie — że kolumna 54 sąsiaduje z kolumną 1. Zaznaczenie
   * jest WSPÓLNE dla obu widoków, bo dotyczy przegródek, a nie sposobu ich narysowania.
   */
  const [widok, setWidok] = useState<'siatka' | 'beben' | 'beben3dv2'>('siatka');
  /**
   * ⚠️⚠️ TRYB WEWNĄTRZ SIATKI, A NIE CZWARTA ZAKŁADKA — poprawione po obejrzeniu ekranu
   * (22.09.2026, użytkownik): „raczej wolałbym po prostu przycisk wewnątrz zakładki siatka".
   * Zakładka sugerowała inny WIDOK na bęben, a to jest ten sam widok w innym trybie pracy.
   */
  const [edycjaKolumn, setEdycjaKolumn] = useState(false);
  const [wybranaZmiana, setWybranaZmiana] = useState<WybranaZmiana | null>(null);
  const [isolatedGroup, setIsolatedGroup] = useState<string | null>(null);
  /**
   * ZAZNACZENIE JEST ZBIOREM I NALEŻY DO JEDNEGO BĘBNA.
   *
   * Zbiór, bo ustawianie towaru po jednej przegródce jest nie do przejścia: kolumna ma 13
   * pozycji, a SPS daje na to tylko regułę „ujednolić całą kolumnę". Ma być tak, że zaznacza
   * się dowolne komórki i ustawia im towar naraz.
   *
   * ⚠️ JEDEN BĘBEN, bo numer kolumny powtarza się między bębnami (`@@unique([drumId, drawerNo])`),
   * a towar nigdy nie jest ten sam w całej maszynie — zaznaczenie przez dwa bębny opisywałoby
   * zamiar, którego nikt nie ma. Kliknięcie w drugim bębnie zaczyna zaznaczenie od nowa.
   */
  const [zaznaczone, setZaznaczone] = useState<ReadonlySet<string>>(() => new Set());
  const [bebenZaznaczenia, setBebenZaznaczenia] = useState<number | null>(null);
  /**
   * WYBRANY MODUŁ (bęben / „PIC" w mowie SPS-a); `null` = wszystkie.
   *
   * ⚠️ Zgłoszone przez obsługę: maszyna z dostawką rysowała obie siatki jedna pod drugą, więc przy
   * dwóch bębnach po 36 kolumn trzeba było przewijać, żeby zobaczyć drugi — a pracuje się zwykle
   * na jednym. Wybór jest FILTREM WIDOKU, nie zmianą zapytania: planogram i tak przychodzi cały,
   * więc przełączanie jest natychmiastowe i nie kosztuje wejścia do bazy.
   *
   * ⚠️⚠️ DO 28.08.2026 BYŁO TU „WSZYSTKIE" JAKO DOMYŚLNE, z uzasadnieniem: „domyślne wskazanie
   * pierwszego bębna ukrywałoby przed kimś, kto o dostawce nie wie, POŁOWĘ maszyny". Użytkownik
   * to ROZSTRZYGNĄŁ inaczej i wprost: „nie chcemy pokazywać od razu wszystkich i w ogóle nie
   * potrzebujemy wyświetlać wszystkich jednocześnie — domyślnie wyświetlony jeden pierwszy".
   *
   * Obawa, która stała za starym zachowaniem, jest zaadresowana ZMIANĄ KONTROLKI, a nie
   * domyślną wartością: przełącznik jest teraz LISTĄ, więc ile modułów ma maszyna widać po jej
   * rozwinięciu, a każdy ma nazwę konstrukcji. Paska z trzema kaflami nie dało się czytać przy
   * maszynie z trzema bębnami — a to jest dziś normalna maszyna, nie wyjątek.
   *
   * ⚠️ `null` ZNACZY TERAZ „JESZCZE NIE WYBRANO", A NIE „WSZYSTKIE". Wartość rozstrzyga się
   * niżej (`aktywnyBeben`) na pierwszym bębnie danych — nie w efekcie, bo efekt ustawiający stan
   * po każdym wczytaniu maszyny kasowałby wybór operatora przy każdym odświeżeniu listy.
   */
  const [wybranyBeben, setWybranyBeben] = useState<number | null>(null);
  const [hover, setHover] = useState<{ c: Compartment; x: number; y: number } | null>(null);
  const [exporting, setExporting] = useState(false);

  const machines = useQuery({
    queryKey: ['machines', 'picker', groupId],
    queryFn: () =>
      listMachines({ groupId: groupId!, page: 1, pageSize: 200, sort: 'vmid', dir: 'asc', onlyOnline: false }),
    enabled: Boolean(groupId),
  });

  const machineFromUrl = searchParams.get('machine');
  /**
   * `?machine=` z URL obowiązuje TYLKO wtedy, gdy ten automat należy do wybranej wyspy.
   *
   * Bez tego przełączenie wyspy zostawiało w URL id automatu z poprzedniej i strona wracała
   * z 404 zamiast pokazać pierwszy automat nowej wyspy — a użytkownik nie widział, że pyta
   * o coś, czego tu nie ma.
   */
  const machineInIsland = machineFromUrl !== null && (machines.data?.rows.some((m) => m.id === machineFromUrl) ?? false);
  const machineId = (machineInIsland ? machineFromUrl : null) ?? machines.data?.rows[0]?.id ?? null;

  // sprzątamy martwy parametr, żeby nie wracał przy odświeżeniu i nie mylił w linku do wysłania
  useEffect(() => {
    if (machineFromUrl !== null && machines.data && !machineInIsland) {
      setSearchParams({}, { replace: true });
    }
  }, [machineFromUrl, machineInIsland, machines.data, setSearchParams]);

  const wyczyscZaznaczenie = useCallback(() => {
    setZaznaczone(new Set());
    setBebenZaznaczenia(null);
    setKotwica(null);
  }, []);

  const setMachineId = useCallback(
    (id: string) => {
      setSearchParams({ machine: id }, { replace: true });
      // zaznaczenie wskazuje przegródki POPRZEDNIEGO automatu — po przełączeniu nie znaczy nic
      wyczyscZaznaczenie();
    },
    [setSearchParams, wyczyscZaznaczenie],
  );

  const { data, isPending, error } = useQuery({
    queryKey: ['planogram', groupId, machineId],
    queryFn: () => fetchPlanogram(groupId!, machineId!),
    enabled: Boolean(groupId && machineId),
  });

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *  UKŁAD SEKCJI KAŻDEGO BĘBNA — wyłącznie w trybie edycji kolumn.
   *
   *  ⚠️⚠️ `useQueries`, A NIE ZAPYTANIE W PĘTLI RENDERUJĄCEJ. Bębnów bywa kilka, a hooka nie wolno
   *  wywołać w pętli — `useQueries` załatwia N zapytań jednym wywołaniem i nie łamie reguł hooków
   *  przy bębnie dołożonym albo zabranym.
   *
   *  ⚠️ `enabled` ZEROWE POZA TRYBEM: poza edycją nie pytamy o nic, bo ekran nie ma na czym tego
   *  pokazać, a odczyt liczy zawartość każdej kolumny osobno.
   * ══════════════════════════════════════════════════════════════════════════════════════════
   */
  const bebnyDoEdycji = edycjaKolumn && groupId && data ? data.summary.drums.filter((b) => b.szafka == null) : [];
  const ukladyKolumn = useQueries({
    queries: bebnyDoEdycji.map((b) => ({
      queryKey: ['kolumny-bebna', groupId, data!.machine.vmid, b.picNumber],
      queryFn: () => fetchKolumnyBebna(groupId!, data!.machine.vmid, b.picNumber),
    })),
  });
  const ukladBebna = (picNumber: number) => {
    const i = bebnyDoEdycji.findIndex((b) => b.picNumber === picNumber);
    return i < 0 ? null : (ukladyKolumn[i]?.data ?? null);
  };

  /**
   * KTÓRY MODUŁ JEST TERAZ POKAZANY — wyliczane, nie trzymane w stanie.
   *
   * ⚠️ WYLICZENIE, A NIE EFEKT USTAWIAJĄCY STAN, i to jest tu cała ostrożność. `useEffect`
   * wpisujący pierwszy bęben po każdym wczytaniu danych kasowałby wybór operatora przy każdym
   * odświeżeniu planogramu (a ten odświeża się sam) — czyli ekran wracałby do pierwszego modułu
   * w środku pracy na drugim.
   *
   * ⚠️ WYBÓR SPOZA TEJ MASZYNY ODPADA — bez tego przejście na automat, który nie ma bębna o tym
   * numerze, dawałoby PUSTY planogram przy poprawnie wczytanych danych. Numery bębnów są lokalne
   * dla maszyny (`PICNUMBER` 1, 3, 5 na jednej i 1 na drugiej), więc stan przeżywa zmianę
   * maszyny i wskazuje wtedy na nic.
   */
  const bebny = data?.summary.drums ?? [];
  const aktywnyBeben =
    bebny.find((b) => b.picNumber === wybranyBeben)?.picNumber ?? bebny[0]?.picNumber ?? null;

  /**
   * Przegródka → jej kolumna. Zaznaczenie trzyma same identyfikatory (żeby przetrwało odświeżenie
   * planogramu), więc wszystko, co pasek zaznaczenia chce o nich powiedzieć — numer kolumny,
   * bęben, reguła jednolitej zawartości — wyjmuje się stąd.
   */
  const gdzieJest = useMemo(() => {
    const m = new Map<string, { kolumna: PlanogramData['drawers'][number]; c: Compartment }>();
    for (const k of data?.drawers ?? []) for (const c of k.compartments) m.set(c.id, { kolumna: k, c });
    return m;
  }, [data]);

  /**
   * KOTWICA ZAKRESU — ostatnia komórka kliknięta bez modyfikatora. Od niej shift liczy prostokąt.
   * `null` po wyczyszczeniu zaznaczenia i po zmianie automatu.
   */
  const [kotwica, setKotwica] = useState<{ picNumber: number; kolumnaIdx: number; rowNo: number } | null>(null);

  /**
   * Najniższy numer bębna tej maszyny — to on jest modułem GŁÓWNYM, reszta to dostawki.
   *
   * ⚠️ Nie da się tego zastąpić porównaniem z jedynką: numeracja bębnów nie jest pozycyjna.
   * Zmierzone 13.08.2026 na bazach klientów — PAGED WS1 ma bębny 1 i 3 (dwa moduły po 36 kolumn),
   * a DBW2 ma JEDEN bęben o numerze 3.
   */
  const najnizszyBeben = useMemo(
    () => (data?.summary.drums ?? []).reduce<number | null>((min, b) => (min === null || b.picNumber < min ? b.picNumber : min), null),
    [data],
  );

  /** Kolumny bieżącego bębna w kolejności rysowania — zakres liczy się po POZYCJI, nie po numerze. */
  const kolumnyBebna = useCallback(
    (picNumber: number) => (data?.drawers ?? []).filter((d) => d.picNumber === picNumber),
    [data],
  );

  /**
   * ⚠️ ZWYKŁY KLIK ZASTĘPUJE ZAZNACZENIE, NIE PRZEŁĄCZA GO — i to jest poprawka zgłoszona wprost:
   * „upierdliwe, jak trzeba odznaczać, żeby wybrać jeden pojedynczy kafelek". Przy przełączaniu
   * wybranie jednej komórki po zaznaczeniu kolumny wymagało dwunastu kliknięć odznaczających.
   *
   * SHIFT bierze prostokąt od kotwicy (kolumny × wiersze), CTRL/CMD przełącza pojedynczą komórkę.
   * Shift był wcześniej odrzucony, bo zaznaczał tekst — dlatego siatka ma teraz `select-none`.
   */
  const klikKomorki = useCallback(
    (c: Compartment, mod: { shift: boolean; toggle: boolean }) => {
      const trafienie = gdzieJest.get(c.id);
      if (trafienie === undefined) return;
      const pic = trafienie.kolumna.picNumber;
      const kolumny = kolumnyBebna(pic);
      const idx = kolumny.findIndex((k) => k.id === trafienie.kolumna.id);
      const innyBeben = bebenZaznaczenia !== null && bebenZaznaczenia !== pic;

      if (mod.shift && kotwica !== null && !innyBeben && kotwica.picNumber === pic) {
        const [a, b] = [Math.min(kotwica.kolumnaIdx, idx), Math.max(kotwica.kolumnaIdx, idx)];
        const [w1, w2] = [Math.min(kotwica.rowNo, c.rowNo), Math.max(kotwica.rowNo, c.rowNo)];
        const zakres = kolumny
          .slice(a, b + 1)
          .flatMap((k) => k.compartments.filter((x) => x.rowNo >= w1 && x.rowNo <= w2).map((x) => x.id));
        // zakres DOKŁADA się do zaznaczenia; kotwica zostaje, żeby dało się rozciągać dalej
        setZaznaczone((prev) => new Set([...prev, ...zakres]));
        setBebenZaznaczenia(pic);
        return;
      }

      if (mod.toggle && !innyBeben) {
        setZaznaczone((prev) => {
          const next = new Set(prev);
          if (!next.delete(c.id)) next.add(c.id);
          return next;
        });
        setBebenZaznaczenia(pic);
        setKotwica({ picNumber: pic, kolumnaIdx: idx, rowNo: c.rowNo });
        return;
      }

      setZaznaczone(new Set([c.id]));
      setBebenZaznaczenia(pic);
      setKotwica({ picNumber: pic, kolumnaIdx: idx, rowNo: c.rowNo });
    },
    [gdzieJest, bebenZaznaczenia, kotwica, kolumnyBebna],
  );

  /**
   * Kliknięcie w NUMER KOLUMNY bierze całą kolumnę — tą samą logiką co komórka:
   * zwykły klik ZASTĘPUJE zaznaczenie, shift/ctrl DOKŁADA (a ctrl na całej zaznaczonej odejmuje).
   *
   * Odejmowanie liczy się dla CAŁEJ kolumny naraz, a nie komórka po komórce: przełączanie każdej
   * z osobna dawałoby przy częściowym zaznaczeniu wynik odwrotny do zamiaru — klik w kolumnę
   * z trzema zaznaczonymi zostawiałby dziesięć zamiast zaznaczyć trzynaście.
   */
  const klikKolumny = useCallback(
    (kolumna: PlanogramData['drawers'][number], mod: { shift: boolean; toggle: boolean }) => {
      const idki = kolumna.compartments.map((c) => c.id);
      if (idki.length === 0) return;
      const inny = bebenZaznaczenia !== null && bebenZaznaczenia !== kolumna.picNumber;

      if ((mod.shift || mod.toggle) && !inny) {
        setZaznaczone((prev) => {
          const next = new Set(prev);
          if (mod.toggle && idki.every((id) => next.has(id))) idki.forEach((id) => next.delete(id));
          else idki.forEach((id) => next.add(id));
          return next;
        });
      } else {
        setZaznaczone(new Set(idki));
      }
      setBebenZaznaczenia(kolumna.picNumber);
      // kotwica na GÓRZE kolumny — shift w komórkę rozciąga wtedy prostokąt w dół, jak w arkuszu
      const idx = kolumnyBebna(kolumna.picNumber).findIndex((k) => k.id === kolumna.id);
      setKotwica({ picNumber: kolumna.picNumber, kolumnaIdx: idx, rowNo: 1 });
    },
    [bebenZaznaczenia, kolumnyBebna],
  );

  /**
   * Rozszerza zaznaczenie do PEŁNYCH kolumn, których dotyka. Najczęstszy ruch po shifcie:
   * zaznaczyło się prostokąt, a chodziło o całe kolumny.
   */
  const zaznaczPelneKolumny = useCallback(() => {
    const kolumny = new Set<string>();
    for (const id of zaznaczone) {
      const k = gdzieJest.get(id)?.kolumna;
      if (k) kolumny.add(k.id);
    }
    setZaznaczone(
      new Set(
        (data?.drawers ?? []).filter((k) => kolumny.has(k.id)).flatMap((k) => k.compartments.map((c) => c.id)),
      ),
    );
  }, [zaznaczone, gdzieJest, data]);

  /**
   * SZUKANY TOWAR — podświetlenie, nie zaznaczenie.
   *
   * ⚠️ ROZDZIAŁ JEST CELEM, NIE SKUTKIEM UBOCZNYM: zaznaczenie decyduje o tym, co zrobi zapis
   * do bazy klienta, a szukanie odpowiada tylko na „gdzie to leży". Wrzucenie znalezionych
   * przegródek do zaznaczenia znaczyłoby, że wyszukiwarka zbroi operację.
   *
   * Zeruje się przy zmianie automatu — identyfikator towaru z poprzedniej maszyny nie trafiłby
   * w nic i pasek szukania kłamałby, że nic nie znaleziono.
   */
  const [szukany, setSzukany] = useState<string | null>(null);
  /** Okno wymiany indeksu — osobne od panelu edycji, bo działa na INDEKSIE, nie na zaznaczeniu. */
  const [podmiana, setPodmiana] = useState(false);
  useEffect(() => setSzukany(null), [machineId]);

  /**
   * ⚠️ TYLKO TOWARY OBECNE W TYM BĘBNIE, nie cały katalog. Lista, w której 90% pozycji nie ma
   * czego podświetlić, zamienia szukanie w zgadywankę „czy to ten automat, czy zła nazwa".
   */
  const towaryWBebnie = useMemo(() => {
    const m = new Map<string, { id: string; name: string; itemNumber: string; ile: number }>();
    for (const k of data?.drawers ?? []) {
      for (const c of k.compartments) {
        if (!c.item) continue;
        const w = m.get(c.item.id);
        if (w) w.ile += 1;
        else m.set(c.item.id, { id: c.item.id, name: c.item.name, itemNumber: c.item.itemNumber, ile: 1 });
      }
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  }, [data]);

  const trafien = towaryWBebnie.find((x) => x.id === szukany)?.ile ?? 0;

  /** Trzy najliczniejsze grupy dostają własny kolor; pozostałe wpadają do „Inne". */
  const groupColor = useMemo(() => {
    const map = new Map<string, { bg: string; ink: string }>();
    (data?.itemGroups ?? []).slice(0, CAT_SLOTS.length).forEach((g, i) => map.set(g.id, CAT_SLOTS[i]!));
    return map;
  }, [data]);

  const colorFor = useCallback(
    (c: Compartment): { bg: string; ink: string } => {
      if (!c.item) return NEUTRAL;
      const fill = c.fillPercent ?? 0;

      if (mode === 'stock') {
        const s = stockStep(fill);
        return { bg: s.bg, ink: s.ink };
      }
      // tryb grup: izolacja pojedynczej grupy albo top-3 + Inne
      if (isolatedGroup) {
        return c.item.groupId === isolatedGroup ? CAT_SLOTS[0]! : NEUTRAL;
      }
      const gid = c.item.groupId;
      return (gid === null ? undefined : groupColor.get(gid)) ?? NEUTRAL;
    },
    [mode, isolatedGroup, groupColor],
  );

  const exportRows = useCallback((): { cols: ExportColumn[]; rows: ExportRow[] } => {
    const cols: ExportColumn[] = [
      /** Nagłówki w języku operatora; nazwy SPS stoją w nawiasie, bo po nich szuka się w bazie. */
      /**
       * ⚠️ BĘBEN PIERWSZY, bo bez niego para (kolumna, wiersz) NIE JEST jednoznaczna: unikat
       * w bazie to `(drumId, drawerNo)`, więc automat z dostawką dawał w eksporcie dwa wiersze
       * o identycznych współrzędnych i różnej treści.
       */
      { key: 'drum', header: t('planogram.drumExport') },
      { key: 'drawer', header: t('planogram.columnExport') },
      { key: 'compartment', header: t('planogram.rowExport') },
      { key: 'itemNumber', header: t('planogram.itemNumber') },
      { key: 'item', header: t('planogram.item') },
      { key: 'group', header: t('planogram.group') },
      { key: 'qty', header: t('planogram.qty') },
      { key: 'max', header: t('planogram.max') },
      { key: 'fill', header: t('planogram.fill') },
    ];
    const rows: ExportRow[] = (data?.drawers ?? []).flatMap((d) =>
      d.compartments.map((c) => ({
        drum: d.picNumber,
        drawer: d.drawerNo,
        compartment: c.compartmentNo,
        itemNumber: c.item?.itemNumber ?? '',
        item: c.item?.name ?? t('planogram.emptySlot'),
        group: c.item?.groupName ?? '',
        qty: c.qtyCurrent ?? '',
        max: c.qtyMax ?? '',
        fill: c.fillPercent === null ? '' : `${c.fillPercent}%`,
      })),
    );
    return { cols, rows };
  }, [data, t]);

  const [mapaPracuje, setMapaPracuje] = useState(false);

  /**
   * MAPA SZAFY DO PDF — rysunek, nie lista.
   *
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ⚠️⚠️ ZAMÓWIONE 02.09.2026 ZE WZORCA ZE STAREGO VENDI: „kolorkami zaznacza co jest na danym
   *  bębnie (…) output tylko w pdf". Istniejący przycisk PDF oddaje TABELĘ przegródek — odpowiada
   *  na pytanie „co gdzie leży" wierszami, a nie rysunkiem szafy, po którym widać wzór rozłożenia.
   *
   *  ⚠️ KOLOR PO TOWARZE ALBO PO GRUPIE — wybór stoi na przycisku, bo obie odpowiedzi są dobre
   *  na różne pytania: kolor po towarze pokazuje, gdzie leży TEN towar, kolor po grupie — gdzie
   *  są rękawice, a gdzie ściernice. Numer w przegródce jest ten sam w obu, więc dwa wydruki tej
   *  samej szafy dają się porównać.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const generujMape = async (tryb: TrybKoloru) => {
    if (!data) return;
    setMapaPracuje(true);
    try {
      const blob = await zlozMapePdf({
        dane: data,
        tryb,
        wyspa: island?.name ?? null,
        teraz: new Date(),
        t,
      });
      downloadBlob(blob, `planogram-mapa-vm${data.machine.vmid}.pdf`);
      toast.success(t('table.exportDone'));
    } catch (err) {
      toast.error(`${t('table.exportFailed')}: ${(err as Error).message}`);
    } finally {
      setMapaPracuje(false);
    }
  };

  const runExport = async (kind: 'csv' | 'xlsx' | 'pdf') => {
    if (!data) return;
    setExporting(true);
    try {
      const { cols, rows } = exportRows();
      const base = `planogram-vm${data.machine.vmid}`;
      const title = `${t('nav.planogram')} — ${data.machine.name} (VMID=${data.machine.vmid})`;
      if (kind === 'csv') downloadBlob(buildCsvBlob(cols, rows), `${base}.csv`);
      if (kind === 'xlsx') downloadBlob(await buildXlsxBlob(cols, rows, title), `${base}.xlsx`);
      if (kind === 'pdf') downloadBlob(await buildPdfBlob(cols, rows, title), `${base}.pdf`);
      toast.success(t('table.exportDone'));
    } catch (err) {
      toast.error(`${t('table.exportFailed')}: ${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  return (
    /*
      Odsunięcia robią miejsce dwóm rzeczom PRZYKLEJONYM DO OKNA: pływającemu paskowi na dole
      i panelowi szczegółów po prawej. Bez nich pasek zasłaniałby ostatni wiersz siatki, a panel
      prawą część bębna — czyli dokładnie to, na co się patrzy.
    */
    <div
      className={cn(
        /**
         * ⚠️ BEZ `max-w-6xl` NA TYM EKRANIE. Siatka ma 54 kolumny i jest głównym powodem, dla
         * którego się tu wchodzi — ograniczanie jej do szerokości artykułu zostawiało na FHD
         * pas pustego tła i zmuszało do przewijania w bok (zgłoszone ze zrzutem). Pozostałe
         * ekrany zostają wąskie; szeroki jest ten, który ma szerokie dane.
         */
        'pg-viz space-y-5 transition-[padding]',
        zaznaczone.size > 1 && 'pb-14',
        zaznaczone.size > 0 && 'lg:pr-[24rem]',
      )}
    >
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('nav.planogram')}</h1>
      </div>

      {/* filtry jednym rzędem nad wizualizacją */}
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={machineId ?? ''}
          onChange={(e) => setMachineId(e.target.value)}
          aria-label={t('planogram.machine')}
          className="h-9 w-72"
        >
          {(machines.data?.rows ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              VMID {m.vmid} — {m.name}
            </option>
          ))}
        </Select>

        {/*
          ⚠️ SZAFKA NIE MA WALCA, i to nie jest uproszczenie. Oba widoki bębna pokazują JEDNĄ
          rzecz, której siatka nie umie: że ostatnia kolumna sąsiaduje z pierwszą, bo bęben jest
          okrągły. Lockerbox jest szafą — drzwiczki 15 nie stykają się z drzwiczkami 1. Zostawienie
          tam walca obiecywałoby sąsiedztwo, którego w maszynie nie ma.
        */}
        <div className="flex overflow-hidden rounded-md border border-border">
          {(((data?.summary.drums ?? []).length > 0 && (data?.summary.drums ?? []).every((b) => b.szafka != null)
            ? (['siatka'] as const)
            : (['siatka', 'beben', 'beben3dv2'] as const)) as readonly ('siatka' | 'beben' | 'beben3dv2')[]).map((w) => (
            <button
              key={w}
              type="button"
              /**
               * ⚠️ ZMIANA WIDOKU CZYŚCI ZAZNACZENIE — ustalone z użytkownikiem. Zaznaczenie
               * zrobione w płaskiej siatce (shiftem, prostokątem) nie ma czytelnego odpowiednika
               * na obwodzie walca i odwrotnie: to samo zaznaczenie znaczy w obu widokach co
               * innego dla oka. Każdy widok zaczyna więc od zera.
               */
              onClick={() => {
                setWidok(w);
                if (w !== widok) {
                  wyczyscZaznaczenie();
                  /** ⚠️ Tryb edycji należy do siatki — na walcu nie ma czego w nim pokazać. */
                  setEdycjaKolumn(false);
                  setWybranaZmiana(null);
                }
              }}
              aria-pressed={widok === w}
              className={cn(
                'px-3 py-1.5 text-xs transition-colors',
                widok === w ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
              )}
            >
              {t(`planogram.view.${w}`)}
            </button>
          ))}
        </div>

        {/*
          Tryby kolorowania dotyczą TYLKO siatki — walec ma własne, trzy stany zatowarowania.
          ⚠️ WARUNEK NA RENDEROWANIU, a nie klasa `hidden` obok `flex`: obie ustawiają `display`,
          więc o zwycięzcy decyduje kolejność w WYGENEROWANYM CSS, a nie kolejność w `className`.
          Dziś wygrywa `hidden` (sprawdzone), ale to szczegół sortowania Tailwinda, nie zamiar.
        */}
        {/*
          ⚠️⚠️ PRZEŁĄCZNIK TRYBU, NIE WIDOKU. Siatka zostaje tą samą siatką — zmienia się to, co
          znaczy w niej kliknięcie. Na czas edycji szerokości przestaje służyć do zaznaczania
          przegród, bo dwa znaczenia tego samego kliknięcia byłyby pułapką przy operacji, która
          kasuje towar.

          ⚠️ NIE PRZY SZAFCE. Lockerbox nie ma kolumn ani kanałów; przycisk, który zawsze odmawia,
          uczy patrzącego, że ekran kłamie.
        */}
        {widok === 'siatka' && !(data?.summary.drums ?? []).every((b) => b.szafka != null) && (
          <Button
            type="button"
            size="sm"
            variant={edycjaKolumn ? 'default' : 'outline'}
            aria-pressed={edycjaKolumn}
            onClick={() => {
              setEdycjaKolumn((v) => !v);
              wyczyscZaznaczenie();
              /** ⚠️ Wyjście z trybu gasi też wybór — inaczej czerwona kolumna zostałaby na ekranie. */
              setWybranaZmiana(null);
            }}
          >
            <Columns3 className="size-4" />
            {t('szer.tryb')}
          </Button>
        )}

        {widok === 'siatka' && !edycjaKolumn && (
          <div className="flex overflow-hidden rounded-md border border-border">
            {(['stock', 'groups'] as ColorMode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  if (m !== 'groups') setIsolatedGroup(null);
                }}
                aria-pressed={mode === m}
                className={cn(
                  'px-3 py-1.5 text-xs transition-colors',
                  mode === m ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
                )}
              >
                {t(`planogram.mode.${m}`)}
              </button>
            ))}
          </div>
        )}

        {/*
          SZUKANIE TOWARU — obok przełącznika widoku, bo działa w obu i dotyczy tego samego bębna.
          Zwykły `select`: przeglądarka pozwala w nim szukać pisaniem, a lista jest krótka, bo
          zawiera wyłącznie towary z tego bębna.
        */}
        {towaryWBebnie.length > 0 && (
          <div className="flex items-center gap-1.5">
            <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <Select
              value={szukany ?? ''}
              onChange={(e) => setSzukany(e.target.value === '' ? null : e.target.value)}
              aria-label={t('planogram.findItem')}
              className="h-9 w-64"
            >
              <option value="">{t('planogram.findItem')}</option>
              {towaryWBebnie.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name} ({x.ile})
                </option>
              ))}
            </Select>
            {szukany !== null && (
              <>
                {/*
                  ⚠️ KRZYŻYK, MIMO ŻE NA LIŚCIE JEST POZYCJA PUSTA. Zgłoszone wprost: wracanie do
                  rozwijanej listy po to, żeby wybrać „nic", jest nieintuicyjne — podświetlenie
                  włącza się jednym ruchem i tak samo ma się wyłączać.
                */}
                <button
                  type="button"
                  onClick={() => setSzukany(null)}
                  aria-label={t('planogram.findClear')}
                  title={t('planogram.findClear')}
                  className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  {t('planogram.findHits').replace('{n}', String(trafien))}
                </span>
              </>
            )}
          </div>
        )}

        <div className="flex-1" />

        {/*
          ⚠️⚠️ WYMIANA INDEKSU — zamówione 09.09.2026: „wypierdalamy całkiem jeden artykuł
          i w te same szuflady pakujemy inny — w tej chwili trzeba to robić ręcznie".

          ⚠️ PRZYCISK PRZY PASKU AUTOMATU, nie w panelu edycji zaznaczenia. Panel działa na
          PRZEGRÓDKACH, które operator zaznaczył; ta operacja działa na INDEKSIE i sama znajduje
          jego przegródki — wejście przez zaznaczanie byłoby dokładnie tą robotą, której ma nie być.

          ⚠️ BEZ INDEKSÓW W AUTOMACIE NIE MA CO WYMIENIAć — przycisk jest wtedy wyszarzony,
          a nie ukryty: brak funkcji i brak danych to dwie różne rzeczy.
        */}
        {machineId !== null && (
          <Button
            variant="outline"
            size="sm"
            disabled={towaryWBebnie.length === 0}
            onClick={() => setPodmiana(true)}
          >
            <Repeat2 />
            {t('podm.przycisk')}
          </Button>
        )}

        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" disabled={exporting || !data} onClick={() => void runExport('csv')} data-export="csv">
            {exporting ? <Loader2 className="animate-spin" /> : <Download />}
            CSV
          </Button>
          <Button variant="outline" size="sm" disabled={exporting || !data} onClick={() => void runExport('xlsx')} data-export="xlsx">
            <FileSpreadsheet />
            {/* ⚠️ „XLSX", nie „Excel" — ta sama nazwa formatu w każdym pasku eksportu (31.08.2026). */}
            XLSX
          </Button>
          <Button variant="outline" size="sm" disabled={exporting || !data} onClick={() => void runExport('pdf')} data-export="pdf">
            <FileText />
            PDF
          </Button>
          <PrzyciskZWyborem
            etykieta={t('planogram.mapa')}
            ikona={LayoutGrid}
            dataExport="mapa"
            tytul={t('planogram.mapaTytul')}
            disabled={mapaPracuje || !data}
            pracuje={mapaPracuje}
            pozycje={[
              { klucz: 'towar', etykieta: t('planogram.mapaPoTowarze'), opis: t('planogram.mapaPoTowarzeOpis') },
              { klucz: 'grupa', etykieta: t('planogram.mapaPoGrupie'), opis: t('planogram.mapaPoGrupieOpis') },
            ]}
            onWybor={(tryb) => void generujMape(tryb)}
          />
        </div>
      </div>

      {/*
        Kolejnosc warunkow ma znaczenie. Wylaczone zapytanie ma w TanStack v5 status 'pending',
        wiec samo `isPending` wieszalo strone na „Ladowanie..." w dwoch realnych przypadkach:
        gdy picker automatow dostal 403 (konto z uprawnieniem `planogram`, ale bez `machines`)
        i gdy wyspa nie ma zadnego automatu. Cisza jest gorsza niz blad — mowimy, co sie stalo.
      */}
      {machines.error && (
        <p className="text-sm text-destructive">
          {machines.error instanceof ApiError && machines.error.status === 403
            ? t('planogram.needsMachinesPermission')
            : `${t('common.error')}: ${(machines.error as Error).message}`}
        </p>
      )}
      {!machines.error && machines.data && machines.data.rows.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('planogram.noMachines')}</p>
      )}
      {!machines.error && machineId !== null && isPending && (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      )}
      {error && (
        <p className="text-sm text-destructive">
          {t('common.error')}: {(error as Error).message}
        </p>
      )}

      {data && (
        <>
          {/*
            Pasek naprawy NAD podsumowaniem, bo mówi o czymś, czego w podsumowaniu nie widać:
            wiersz niepełny liczy się w statystykach normalnie, a mimo to potrafi zamknąć bęben
            w SPS. Znika sam, gdy nie ma czego naprawiać.
          */}
          {groupId && machineId && <PasekNaprawy groupId={groupId} machineId={machineId} />}
          <PlanogramSummary data={data} />

          <Card>
            <CardContent className="space-y-4 pt-5">
              {/*
                JEDNA SIATKA NA BĘBEN. Zwykły automat ma jeden i wygląda jak dotąd; automat
                z dostawką dostaje dwie siatki, bo kolumna „1" istnieje w każdym bębnie osobno
                (`@@unique([drumId, drawerNo])`). Nagłówek bębna pokazujemy TYLKO gdy jest ich
                więcej niż jeden — przy jednym byłby napisem bez treści.
              */}
              {/*
                WYBÓR MODUŁU — WIDOCZNY ZAWSZE, także przy jednym bębnie.
                ⚠️ Przy jednym module przełącznik jest WYSZARZONY I NIEKLIKALNY, a nie schowany.
                Zgłoszone wprost: pasek ma stać w tym samym miejscu niezależnie od maszyny, żeby
                było widać, ILE modułów ta maszyna ma. Znikający element odpowiadał na to pytanie
                swoją nieobecnością, czyli wcale — a operator przechodzi między automatami i to
                po tym pasku poznaje, że gdzieś jest dostawka, a gdzieś jej nie ma.
                Pozycji „Wszystkie" wtedy nie ma: przy jednym module byłaby tym samym, co on sam.
              */}
              {/*
                ⚠️ ROZJAZD DRZWICZEK Z PRZEGRÓDKAMI POKAZUJEMY, NIE ZAMALOWUJEMY. Drzwiczki bez
                przegródki znaczą, że operator włoży towar tam, gdzie portal go nie widzi — a to
                jest gorsze niż brzydki rysunek. Puste przy bębnie i przy zdrowej szafce.
              */}
              {data.summary.drums.flatMap((b) => b.rozbieznosciSzafki ?? []).length > 0 && (
                <div className="mb-2 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
                  <b>Konfiguracja szafki nie spina się z zawartością.</b>{' '}
                  {[...new Set(data.summary.drums.flatMap((b) => b.rozbieznosciSzafki ?? []))].join('; ')}. Rysunek
                  pokazuje tylko drzwiczki, o których wie maszyna.
                </div>
              )}
              {data.summary.drums.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-wybor-bebna>
                  <label className="text-xs text-muted-foreground" htmlFor="wybor-modulu">
                    {t('planogram.modulePick')}
                  </label>
                  <Select
                    id="wybor-modulu"
                    className="h-8 max-w-full text-xs sm:w-80"
                    value={aktywnyBeben === null ? '' : String(aktywnyBeben)}
                    onChange={(e) => setWybranyBeben(Number(e.target.value))}
                    /**
                     * ⚠️ PRZY JEDNYM MODULE LISTA JEST WYSZARZONA, A NIE SCHOWANA — to zostaje
                     * z poprzedniej wersji i było zgłoszone wprost: kontrolka ma stać w tym samym
                     * miejscu niezależnie od maszyny, bo operator przechodzi między automatami
                     * i po niej poznaje, że gdzieś jest dostawka, a gdzieś jej nie ma. Element
                     * znikający odpowiada na to pytanie swoją nieobecnością, czyli wcale.
                     */
                    disabled={data.summary.drums.length === 1}
                    title={data.summary.drums.length === 1 ? t('planogram.moduleOnlyOne') : undefined}
                  >
                    {data.summary.drums.map((b) => (
                      <option key={b.picNumber} value={b.picNumber}>
                        {/*
                          NAZWA KONSTRUKCJI I ROZMIAR, a numer bębna w nawiasie. Obsługa mówi
                          „DynaBox 13/54", nie „bęben 3" — ale numer musi zostać widoczny, bo
                          tylko nim adresuje SPS i tylko po nim da się to zgłosić dalej.
                        */}
                        {`${b.label} (${t('planogram.drum').replace('{n}', String(b.picNumber))})`}
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              {data.summary.drums
                .filter((b) => b.picNumber === aktywnyBeben)
                .map((beben) => {
                const szuflady = data.drawers.filter((d) => d.picNumber === beben.picNumber);
                /**
                 * ⚠️ WYSOKOŚĆ SIATKI BIERZE SIĘ Z TEGO MODUŁU, A NIE Z AUTOMATU.
                 *
                 * ═════════════════════════════════════════════════════════════════════════════
                 *  ⚠️⚠️ ZGŁOSZONE Z PESA (28.08.2026): na PESA-2 szafka „Temrex LockerBox ·
                 *  7 drzwiczek (5/1/1)" narysowała się na TRZYNAŚCIE wierszy zamiast pięciu.
                 *  Trzynaście to wysokość najwyższego modułu TEJ MASZYNY — `summary.rows` jest
                 *  maksimum po wszystkich bębnach — a najdłuższa kolumna szafki ma 5 drzwiczek.
                 *  Na PESA-3 tego nie było widać, bo tam moduł jest jeden i maksimum automatu
                 *  równa się jego własnej wysokości.
                 *
                 *  ⚠️ OSIEM NADMIAROWYCH WIERSZY TO NIE JEST BRZYDKI RYSUNEK, TYLKO NIEPRAWDA:
                 *  operator dostaje miejsca, których za tymi drzwiczkami nie ma. API liczy
                 *  wysokość osobno dla każdego modułu (`drums[].rows`) i etykieta modułu jedzie
                 *  z niej od początku — tylko do siatki szła stara, automatowa liczba.
                 *
                 *  ⚠️ ZERA NIE PODMIENIAMY NA WYSOKOŚĆ AUTOMATU. Moduł bez zapisanych przegródek
                 *  ma się narysować jako pusty — podstawienie `summary.rows` byłoby dokładnie tym
                 *  błędem, tylko rzadszym.
                 * ═════════════════════════════════════════════════════════════════════════════
                 */
                const wierszeModulu = beben.rows;
                return (
                  <div key={beben.picNumber} className="space-y-2">
                    {data.summary.drums.length > 1 && (
                      <div className="flex flex-wrap items-baseline gap-2 text-xs">
                        <span className="font-semibold">{beben.label}</span>
                        <span className="text-muted-foreground">
                          {t('planogram.drum').replace('{n}', String(beben.picNumber))}
                        </span>
                        {/*
                          DWA RÓŻNE POJĘCIA i etykieta ma je rozdzielać: DOSTAWKA to dodatkowa
                          szafa z własnym bębnem (rośnie liczba kolumn), a POSZERZENIE to blacha
                          w bębnie (kolumna zajmuje 2–3 miejsca, kolumn jest mniej). Operator zna
                          tę szafę jako „dostawkę", nie jako „bęben 2".

                          Numer zostaje jako FAKT — nim adresuje SPS. To, że numery > 1 pochodzą
                          z dostawek, wynika z tego, czym dostawka jest, a nie z kolumny w bazie,
                          więc stoi OBOK numeru jako odczytanie, nie zamiast niego.
                        */}
                        <Badge variant="neutral" className="px-1.5 py-0 text-[10px] leading-4">
                          {/*
                            ⚠️ GŁÓWNY = NAJNIŻSZY NUMER, a nie „numer 1". Numeracja bębnów nie jest
                            pozycyjna: po przekładkach maszyna potrafi mieć bębny 1 i 3 (zmierzone na
                            PAGED WS1), a nawet jeden jedyny bęben o numerze 3 (DBW2). Warunek
                            „picNumber === 1" nazywał wtedy dostawką moduł, który jest w tej maszynie
                            pierwszy — albo jedyny.

                            ⚠️ TO NADAL JEST HEURYSTYKA, tylko lepsza. Dostawka różni się tym, że nie
                            ma komputera ani ekranu — sam bęben — a tego w bazie nie ma żadna kolumna:
                            `PICAddressMode` trzyma tylko `MODETYPE`, `PICNUMBER`, `THIRDPARTYCOMPORT`
                            i `ISFREEISSUE`. Najniższy numer jest najbliższym przybliżeniem „ten
                            z ekranem", ale gdyby kiedyś wyszło inaczej, poprawka jest tutaj.
                          */}
                          {beben.picNumber === najnizszyBeben ? t('planogram.drumMain') : t('planogram.drumExtension')}
                        </Badge>
                        <span className="text-muted-foreground">
                          {/*
                            Liczba PO rzeczowniku („kolumn: 3"), nie przed. Polska odmiana ma trzy
                            formy (1 kolumna / 3 kolumny / 34 kolumn), więc „{n} kolumn" jest błędne
                            dla 2–4. Ustawienie liczby po dwukropku jest poprawne dla każdej wartości
                            i nie wymaga tablicy odmian w dwóch językach.
                          */}
                          {t('planogram.columnsShort')} {beben.columns} ·{' '}
                          {beben.slotsCertain
                            ? t('planogram.slotsExact').replace('{n}', String(beben.slots))
                            : t('planogram.slotsAtMost').replace('{n}', String(beben.slots))}
                        </span>
                      </div>
                    )}
                    {widok === 'beben3dv2' ? (
                      /**
                       * ⚠️ ŁADOWANY LENIWIE, i to był warunek dołożenia `three` do projektu.
                       * Biblioteka waży ~150 kB po kompresji; wciągana z głównym pakietem
                       * kosztowałaby KAŻDE wejście do portalu, także tych, którzy planogramu
                       * nigdy nie otwierają. `lazy` sprowadza ją dopiero przy tej zakładce.
                       */
                      /**
                       * ⚠️ GRANICA BŁĘDU JEST TU OBOWIĄZKOWA, nie ostrożnościowa. Zgłoszone
                       * z Firefoksa: „mruga, że zaczyna ładować, a potem cała biała strona".
                       * React przy niezłapanym wyjątku odmontowuje CAŁE drzewo — więc usterka
                       * jednego widoku zabierała ze sobą portal razem z nawigacją. WebGL potrafi
                       * odmówić z powodów, na które nie mamy wpływu (sterownik, ustawienia
                       * przeglądarki, brak akceleracji), i musi to znaczyć komunikat w ramce,
                       * a nie pusty dokument.
                       */
                      <GranicaBledu
                        tytul={t('planogram.d3v2.awaria')}
                        opis={t('planogram.d3v2.awariaOpis')}
                      >
                        <Suspense
                          fallback={
                            <div className="flex h-[460px] items-center justify-center rounded-lg border border-border bg-muted/20 text-sm text-muted-foreground">
                              <Loader2 className="mr-2 size-4 animate-spin" />
                              {t('planogram.d3v2.ladowanie')}
                            </div>
                          }
                        >
                          <Beben3DV2
                            drawers={szuflady}
                            rows={wierszeModulu}
                            zaznaczone={zaznaczone}
                            szukany={szukany}
                            onToggle={klikKomorki}
                            /**
                             * ⚠️ WYJŚCIE NA WIDOK, KTÓRY DZIAŁA WSZĘDZIE. Przeglądarka bez WebGL
                             * nie jest przypadkiem teoretycznym — zmierzone na Firefoksie
                             * użytkownika („Exhausted GL driver options"). Sama instrukcja
                             * zostawiałaby go z ekranem, na którym nie da się zrobić nic.
                             */
                            onStarszyWidok={() => setWidok('beben')}
                          />
                        </Suspense>
                      </GranicaBledu>
                    ) : widok === 'beben' ? (
                      <Beben3D
                        drawers={szuflady}
                        rows={wierszeModulu}
                        zaznaczone={zaznaczone}
                        szukany={szukany}
                        onToggle={klikKomorki}
                      />
                    ) : (
                      <DrumGrid
                        drawers={szuflady}
                        rows={wierszeModulu}
                        colorFor={colorFor}
                        zaznaczone={zaznaczone}
                        szukany={szukany}
                        onToggle={klikKomorki}
                        onToggleColumn={klikKolumny}
                        edycja={
                          edycjaKolumn && ukladBebna(beben.picNumber) !== null
                            ? {
                                poszerzenieZ: (zostaje) =>
                                  ukladBebna(beben.picNumber)!.poszerzenia.find((x) => x.zostaje === zostaje) ?? null,
                                zwezenieDla: (zostaje) =>
                                  ukladBebna(beben.picNumber)!.zwezenia.find((x) => x.zostaje === zostaje) ?? null,
                                ginaca:
                                  wybranaZmiana?.co === 'poszerz' && wybranaZmiana.picNumber === beben.picNumber
                                    ? wybranaZmiana.p.kolumna
                                    : null,
                                onPoszerz: (p) => setWybranaZmiana({ co: 'poszerz', p, picNumber: beben.picNumber }),
                                onZwez: (p) => setWybranaZmiana({ co: 'zwez', p, picNumber: beben.picNumber }),
                              }
                            : undefined
                        }
                        onHover={(cc, el) => {
                          if (!cc || !el) return setHover(null);
                          const r = el.getBoundingClientRect();
                          /**
                           * ⚠️ POZYCJĘ PRZYCINAMY DO OKNA — i to jest poprawka zgłoszonego
                           * „dolny pasek skacze góra-dół". Podpowiedź jest `fixed`, więc przy
                           * komórce blisko prawej krawędzi wystawała poza okno, przeglądarka
                           * dokładała POZIOMY pasek przewijania, a ten podnosił przyklejony
                           * do dołu pasek zaznaczenia o jego wysokość. Wyglądało to jak
                           * miejsce rezerwowane na pasek adresu przy najechaniu na link —
                           * i stąd wzięło się zgłoszenie, mimo że żadnego linku tam nie ma.
                           */
                          const polSzerokosci = 140;
                          const x = Math.min(
                            Math.max(r.left + r.width / 2, polSzerokosci),
                            window.innerWidth - polSzerokosci,
                          );
                          setHover({ c: cc, x, y: r.top });
                        }}
                      />
                    )}
                  </div>
                );
              })}

              {/*
                ⚠️ PASEK POD SIATKĄ, NIE NAD NIĄ. Ostrzeżenie jest już NA siatce — kolumna do
                wchłonięcia ma czerwoną obwódkę razem z towarem, który w niej leży. Pasek dokłada
                liczby i pyta, więc musi stać po tym, co opisuje.
              */}
              {wybranaZmiana !== null && groupId && data && (
                <PasekEdycjiKolumn
                  groupId={groupId}
                  vmid={data.machine.vmid}
                  poziomow={ukladBebna(wybranaZmiana.picNumber)?.poziomow ?? 0}
                  wybrana={wybranaZmiana}
                  onZamknij={() => setWybranaZmiana(null)}
                />
              )}

              {/* Legenda kolorów siatki; walec ma własną, bo pokazuje trzy stany, a nie skalę. */}
              {widok === 'siatka' && !edycjaKolumn && (
                <Legend
                  mode={mode}
                  data={data}
                  groupColor={groupColor}
                  isolatedGroup={isolatedGroup}
                  onIsolate={setIsolatedGroup}
                />
              )}
            </CardContent>
          </Card>

          {/*
            Kolumny o przeskoku > 3 to ostrzeżenie, nie ozdoba: albo bęben ma blachę szerszą,
            niż widzieliśmy, albo w bazie klienta brakuje wiersza dla istniejącego miejsca.
            Cisza w tym miejscu znaczyłaby, że planogram pokazuje mniej miejsc, niż automat ma.
          */}
          {data.summary.suspiciousColumns.length > 0 && (
            <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 p-4 text-sm">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              <span>
                {t('planogram.suspiciousColumns').replace('{list}', data.summary.suspiciousColumns.join(', '))}
              </span>
            </div>
          )}

          <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
            <Info className="mt-0.5 size-4 shrink-0" />
            <div className="space-y-2">
              <p>{t('planogram.orientationNotice')}</p>
              <p>{t('planogram.stockNotice')}</p>
            </div>
          </div>

          {/*
            ⚠️ PASEK DOPIERO OD DWÓCH PRZEGRÓDEK. Przy jednej cały opis jest w panelu bocznym,
            a pasek byłby drugim miejscem mówiącym „zaznaczono: 1" i zabierałby ostatni wiersz
            siatki. Panel otwiera się już przy pierwszej — bo to on odpowiada za zmiany.
          */}
          {zaznaczone.size > 1 && (
            <PasekZaznaczenia
              zaznaczone={zaznaczone}
              gdzieJest={gdzieJest}
              onZaznaczKolumny={zaznaczPelneKolumny}
              onWyczysc={wyczyscZaznaczenie}
            />
          )}
          {podmiana && machineId !== null && (
            <PodmianaIndeksu
              groupId={groupId}
              machineId={machineId}
              obecne={towaryWBebnie}
              onZamknij={() => setPodmiana(false)}
            />
          )}
          {zaznaczone.size > 0 && machineId !== null && (
            <PanelEdycji
              groupId={groupId}
              machineId={machineId}
              zaznaczone={zaznaczone}
              gdzieJest={gdzieJest}
              wieleBebnow={data.summary.drums.length > 1}
              onZamknij={wyczyscZaznaczenie}
            />
          )}
        </>
      )}

      {/*
        DYMEK PODGLĄDU — W PORTALU DO `body` I ZAWSZE ZAMONTOWANY.
        ⚠️⚠️ TO JEST POPRAWKA ZGŁOSZENIA „w chromium cały ekran skacze góra/dół, gdy kursor jest
        między dwoma kafelkami" (2026-08-17; Chrome i Edge tak, Firefox nie). Dymek był DZIECKIEM
        STRONY, czyli wnętrza `<main className="flex-1 overflow-y-auto">` z `AppShell` — a to jest
        kontener przewijania. Na granicy dwóch kafelków kursor co piksel wychodzi z jednego
        i wchodzi w drugi, więc ten węzeł był DODAWANY I USUWANY kilka razy na sekundę WEWNĄTRZ
        scrollera. Chromium przy każdej takiej zmianie DOM-u przelicza zakotwiczenie przewijania
        (scroll anchoring) i potrafi skorygować `scrollTop`; korekta przesuwa siatkę pod kursorem,
        więc pod kursorem ląduje drugi kafelek — i pętla się zamyka. Firefox zakotwicza inaczej
        i dlatego tam objawu nie ma.
        ⚠️ DWIE ZMIANY, KAŻDA ZAMYKA INNĄ DROGĘ. Portal do `body` wyprowadza węzeł ZE scrollera
        (mutacje przestają go dotyczyć, a `body` jest przy okazji pewnym blokiem zawierającym dla
        `position: fixed`). Stałe zamontowanie usuwa samo dodawanie i usuwanie: przy przejściu
        między kafelkami zmieniają się WYŁĄCZNIE `left`/`top`, więc nie ma mutacji do zakotwiczania.
        ⚠️ NIE UŻYWAMY `overflow-anchor: none` na `main`. Wyłączyłoby zakotwiczenie dla CAŁEJ
        aplikacji, czyli zabrało przydatne zachowanie (utrzymanie pozycji, gdy treść nad nią rośnie)
        po to, żeby naprawić jeden dymek.
      */}
      {createPortal(
        <div
          role="tooltip"
          aria-hidden={hover === null}
          className="pointer-events-none fixed z-50 max-w-[280px] -translate-x-1/2 -translate-y-full rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs shadow-lg"
          style={{
            left: hover?.x ?? 0,
            top: (hover?.y ?? 0) - 6,
            /** `visibility`, nie `display`: element zostaje w drzewie, więc nic się nie przemontowuje. */
            visibility: hover === null ? 'hidden' : 'visible',
          }}
        >
          {hover === null ? null : hover.c.item ? (
            <>
              <div className="font-medium">{hover.c.item.name}</div>
              <div className="text-muted-foreground">
                {hover.c.item.groupName ?? '—'} · {hover.c.qtyCurrent}/{hover.c.qtyMax} {hover.c.item.unit}
                {hover.c.fillPercent !== null && ` · ${hover.c.fillPercent}%`}
              </div>
            </>
          ) : (
            <span className="text-muted-foreground">{t('planogram.emptySlot')}</span>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─────────────────────────────── siatka bębna ────────────────────────────────

/**
 * BĘBEN: kolumny = `drawers`, wiersze = przegródki. CSS grid, bo tylko on wyraża komórkę
 * zajmującą dwa albo trzy miejsca bez rozjeżdżania się reszty rzędu.
 *
 * ⚠️ JEDNA SIATKA = JEDEN BĘBEN, i to jest poprawka błędu. Numer kolumny jest unikalny dopiero
 * w parze z bębnem (`@@unique([drumId, drawerNo])`), więc automat z dostawką ma kolumnę „1"
 * w każdym bębnie. Wcześniej wszystkie szuflady automatu szły w jedną siatkę: kolumny o tym
 * samym numerze zlewały się w geometrii, a szerokości brały się z sąsiedztwa między bębnami,
 * którego fizycznie nie ma. Na Donaldsonie nie było tego widać — tam każdy automat ma jeden bęben.
 */
function DrumGrid({
  drawers,
  rows: rowCount,
  colorFor,
  zaznaczone,
  szukany,
  onToggle,
  onToggleColumn,
  onHover,
  edycja,
}: {
  drawers: PlanogramData['drawers'];
  rows: number;
  colorFor: (c: Compartment) => { bg: string; ink: string };
  zaznaczone: ReadonlySet<string>;
  szukany: string | null;
  onToggle: (c: Compartment, mod: { shift: boolean; toggle: boolean }) => void;
  onToggleColumn: (kolumna: PlanogramData['drawers'][number], mod: { shift: boolean; toggle: boolean }) => void;
  onHover: (c: Compartment | null, el: HTMLElement | null) => void;
  /**
   * ⚠️⚠️ TRYB EDYCJI KOLUMN DOKŁADA ZNAKI DO TEJ SIATKI, a nie rysuje drugiej. Dzięki temu widać
   * od razu, jaki towar leży w kolumnie, którą się właśnie poświęca — a to jedyna rzecz, która
   * o tym rozstrzyga. `undefined` = zwykła siatka i ani jednego dodatkowego piksela.
   */
  edycja?: {
    /**
     * ⚠️⚠️ SZUKAMY PO KOLUMNIE, KTÓRA ZOSTAJE, a nie po wchłanianej — i to nie jest wygoda.
     * Portal oddaje wyłącznie złączenia możliwe WEWNĄTRZ sekcji, więc na ostatniej kolumnie sekcji
     * ta funkcja zwraca `null` sama z siebie. Siatka nie musi wiedzieć nic o sekcjach: uchwyt albo
     * jest, albo go nie ma, a granicy nie da się przeoczyć, bo nie ma czego kliknąć.
     */
    poszerzenieZ: (zostaje: number) => Propozycja | null;
    zwezenieDla: (zostaje: number) => Propozycja | null;
    /** Kolumna zaznaczona do wchłonięcia — jej przegrody dostają czerwoną obwódkę. */
    ginaca: number | null;
    onPoszerz: (p: Propozycja) => void;
    onZwez: (p: Propozycja) => void;
  };
}) {
  const { t } = useI18n();

  /**
   * Szerokość RYSOWANA jest przycięta do trzech miejsc, ale liczba w nagłówku pozostaje
   * prawdziwa. Przeskok o dziesięć miejsc znaczy raczej brakujący wiersz w bazie klienta niż
   * blachę na dziesięć pozycji — rysowanie go w skali rozwaliłoby siatkę i uwiarygodniło błąd.
   */
  const kolumny = useMemo(
    () => drawers.map((d) => ({ ...d, visual: Math.min(d.span, MAX_VISUAL_SPAN) })),
    [drawers],
  );
  const miejscaRysowane = kolumny.reduce((a, k) => a + k.visual, 0);

  /**
   * Komórki po POZYCJI W KOLUMNIE (`rowNo`), nigdy po pozycji w tablicy i nigdy po numerze SPS.
   *
   * ⚠️ Do 2026-08-10 kluczem był `compartmentNo` i dla bębna natywnego działało, bo tam obie
   * liczby są równe. DynaBox je rozdziela: pierwsza kolumna trzyma przegródki 1, 55, 109 … 649,
   * więc siatka szukała wiersza 2 pod numerem 2 i nie znajdowała nic, a wysokość wychodziła 702.
   */
  const poWierszach = useMemo(() => {
    const m = new Map<string, Map<number, Compartment>>();
    for (const d of drawers) m.set(d.id, new Map(d.compartments.map((c) => [c.rowNo, c])));
    return m;
  }, [drawers]);

  const wiersze = Array.from({ length: rowCount }, (_, i) => i + 1);

  return (
    /*
      ⚠️ `select-none` NA SIATCE i to jest warunek działania shiftu. Shift+klik w przeglądarce
      zaznacza tekst między klikniętymi miejscami — to był pierwotny powód, dla którego shift
      odrzucono na rzecz przełączania. Bez tej klasy zakres zaznaczałby się razem z podświetlonym
      na niebiesko tekstem całej siatki.
    */
    <div className="select-none overflow-x-auto">
      <div
        className="grid gap-[2px]"
        style={{
          gridTemplateColumns: `${LABEL_W}px repeat(${miejscaRysowane}, ${SLOT_W}px)`,
          width: 'max-content',
        }}
      >
        {/*
          NAGŁÓWEK JEST PRZYCISKIEM: kliknięcie numeru kolumny zaznacza całą kolumnę.
          Stare Vendi otwierało w tym miejscu okienko z formularzem — tutaj kliknięcie ma po
          prostu zaznaczać, bo co dalej zrobić z zaznaczeniem, mówi pasek na dole.
        */}
        <div className="sticky left-0 z-10 bg-card" />
        {kolumny.map((k) => {
          const idki = k.compartments.map((c) => c.id);
          const calaZaznaczona = idki.length > 0 && idki.every((id) => zaznaczone.has(id));
          const czesciowo = !calaZaznaczona && idki.some((id) => zaznaczone.has(id));
          const poszerz = edycja?.poszerzenieZ(k.drawerNo) ?? null;
          const zwez = edycja?.zwezenieDla(k.drawerNo) ?? null;
          return (
            <div key={k.id} className="relative" style={{ gridColumn: `span ${k.visual}` }}>
            <button
              type="button"
              onClick={(e) => onToggleColumn(k, { shift: e.shiftKey, toggle: e.ctrlKey || e.metaKey })}
              disabled={idki.length === 0}
              aria-pressed={calaZaznaczona}
              className={cn(
                'flex items-baseline justify-center gap-1 rounded-t-[4px] pb-1 pt-0.5 text-[11px] transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                calaZaznaczona
                  ? 'bg-primary font-medium text-primary-foreground'
                  : czesciowo
                    ? 'bg-primary/15 text-foreground'
                    : 'text-muted-foreground enabled:hover:bg-accent',
                idki.length === 0 && 'cursor-not-allowed opacity-40',
                'w-full',
              )}
              title={[
                t('planogram.selectColumnHint'),
                /**
                 * ⚠️ REGUŁA JEDNOLITEJ ZAWARTOŚCI W PODPOWIEDZI NAGŁÓWKA, bo tu zaczyna się
                 * zaznaczenie całej kolumny. Zmierzone na DBW2: przy tej fladze skasowanie
                 * jednej przegródki zabiera wszystkie trzynaście.
                 */
                k.uniformContent ? t('planogram.uniformColumnHint') : null,
                k.spanAssumed
                  ? t('planogram.lastColumnAssumed')
                  : k.span > 1
                    ? t('planogram.wide').replace('{n}', String(k.span))
                    : null,
              ]
                .filter(Boolean)
                .join('\n')}
            >
              <span className="tabular-nums">{k.drawerNo}</span>
              {/*
                Reguła jedzie ZNAKIEM, nie samym kolorem — nagłówek zmienia tło od zaznaczenia,
                więc drugi sens na tym samym kanale byłby nie do odczytania.
              */}
              {k.uniformContent && <Link2 className="size-2.5 shrink-0 opacity-80" aria-hidden />}
              {k.span > 1 && (
                <Badge variant="neutral" className="px-1 py-0 text-[9px] leading-4">
                  ×{k.span}
                </Badge>
              )}
            </button>

            {/*
              ⚠️⚠️ UCHWYT SIADA NA GRANICY MIĘDZY KOLUMNAMI, bo tam właśnie jest blacha, którą się
              wyjmuje. Istnieje wyłącznie wewnątrz sekcji: portal oddaje tylko złączenia możliwe
              w sekcji, więc na jej ostatniej kolumnie `poszerzenieZ` zwraca `null` i przez granicę
              nie ma czego kliknąć. Zakaz przestaje być komunikatem błędu i staje się brakiem
              przycisku.
            */}
            {poszerz !== null && (
              <button
                type="button"
                title={t('szer.poszerz')}
                aria-label={`${t('szer.poszerz')} ${k.drawerNo}`}
                onClick={() => edycja?.onPoszerz(poszerz)}
                className={cn(
                  'absolute -right-[9px] top-0 z-20 flex size-4 items-center justify-center rounded-full border bg-card',
                  poszerz.poziom === 'GINIE_TOWAR'
                    ? 'border-[var(--destructive)] text-[var(--destructive)]'
                    : 'border-border text-muted-foreground hover:bg-accent',
                )}
              >
                <Maximize2 className="size-2.5" />
              </button>
            )}

            {/* ⚠️ Zwężenie tylko na kolumnie poszerzonej — inaczej nie ma czego oddać. */}
            {zwez !== null && (
              <button
                type="button"
                title={t('szer.zwez')}
                aria-label={`${t('szer.zwez')} ${k.drawerNo}`}
                onClick={() => edycja?.onZwez(zwez)}
                className="absolute -right-[9px] top-0 z-20 flex size-4 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:bg-accent"
              >
                <Minimize2 className="size-2.5" />
              </button>
            )}
            </div>
          );
        })}

        {wiersze.map((rowNo) => (
          <Fragment key={rowNo}>
            {/*
              Etykieta wiersza PRZYKLEJONA do lewej: przy 36 kolumnach siatka jedzie w poziomie
              i bez tego po przewinięciu nie wiadomo, który to wiersz.
            */}
            <div className="sticky left-0 z-10 flex items-center justify-end bg-card pr-2 text-[11px] tabular-nums text-muted-foreground">
              {rowNo}
            </div>
            {kolumny.map((k) => {
              const c = poWierszach.get(k.id)?.get(rowNo) ?? null;
              return (
                <Cell
                  ginie={edycja?.ginaca === k.drawerNo}
                  key={`${k.id}-${rowNo}`}
                  c={c}
                  drawerNo={k.drawerNo}
                  rowNo={rowNo}
                  span={k.visual}
                  colorFor={colorFor}
                  selected={c !== null && zaznaczone.has(c.id)}
                  szukany={szukany}
                  onSelect={onToggle}
                  onHover={onHover}
                />
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────── podsumowanie ────────────────────────────────

function PlanogramSummary({ data }: { data: PlanogramData }) {
  const { t } = useI18n();
  const s = data.summary;
  const items = [
    /**
     * „Kolumny × wiersze" i OSOBNO liczba miejsc bębna. Same kolumny (34) nie mówią, jak duży
     * jest bęben, bo poszerzone zajmują po dwa albo trzy miejsca — a operator liczy miejsca.
     */
    [t('planogram.grid'), `${s.columns} × ${s.rows}`],
    /**
     * ⚠️ „Co najwyżej", gdy w sumie siedzi dwuznaczny przeskok. Rozmiar bębna jest wielkością
     * fizyczną i okrągłą — podanie 40 dla bębna 36-miejscowego brzmiałoby jak pomiar.
     */
    [
      /**
       * ⚠️ ETYKIETA ZALEŻY OD LICZBY BĘBNÓW. „Miejsc w bębnie" przy automacie z dostawką byłoby
       * nieprawdą: liczba jest wtedy SUMĄ dwóch bębnów i żadnego z nich nie opisuje. Rozmiary
       * pojedynczych bębnów stoją nad ich siatkami.
       */
      s.drums.length > 1 ? t('planogram.slotsTotal') : t('planogram.slots'),
      s.slotsCertain ? String(s.slots) : t('planogram.slotsAtMost').replace('{n}', String(s.slots)),
    ],
    [t('planogram.compartments'), `${s.occupied}/${s.compartments}`],
    [t('planogram.stock'), `${s.qtyCurrent}/${s.qtyMax}`],
    [t('machines.fill'), s.fillPercent === null ? '—' : `${s.fillPercent}%`],
    // prog MUSI byc w etykiecie: ten sam napis w Alertach znaczy "<=1 szt.", tu "<=25% zapelnienia"
    [t('planogram.lowWithThreshold').replace('{pct}', String(s.lowThresholdPercent)), String(s.lowCount)],
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border bg-card px-4 py-3">
      <div className="text-sm">
        <span className="font-medium">{data.machine.name}</span>
        <span className="ml-2 font-mono text-xs text-muted-foreground">VMID {data.machine.vmid}</span>
      </div>
      {items.map(([k, v]) => (
        <div key={k} className="text-sm">
          <span className="text-muted-foreground">{k}: </span>
          <span className="font-medium tabular-nums">{v}</span>
        </div>
      ))}
      <Badge variant={data.machine.online ? 'success' : 'neutral'}>{data.machine.online ? 'online' : 'offline'}</Badge>
    </div>
  );
}

// ─────────────────────────────── legenda ─────────────────────────────────────

function Legend({
  mode,
  data,
  groupColor,
  isolatedGroup,
  onIsolate,
}: {
  mode: ColorMode;
  data: PlanogramData;
  groupColor: Map<string, { bg: string; ink: string }>;
  isolatedGroup: string | null;
  onIsolate: (id: string | null) => void;
}) {
  const { t } = useI18n();

  if (mode === 'stock') {
    return (
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {STOCK_STEPS.map((s) => {
          const Icon = s.icon;
          return (
            <span key={s.key} className="flex items-center gap-1.5">
              {/* stan NIGDY nie jedzie samym kolorem — zawsze ikona + etykieta */}
              <span
                className="inline-flex size-4 items-center justify-center rounded-[2px]"
                style={{ background: s.bg, color: s.ink }}
              >
                <Icon className="size-2.5" />
              </span>
              {t(`planogram.${s.key}`)}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {data.itemGroups.map((g, i) => {
          const color = groupColor.get(g.id);
          const isolated = isolatedGroup === g.id;
          const swatch = isolated ? { bg: 'var(--pg-cat-1)' } : (color ?? { bg: 'var(--pg-other)' });
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => onIsolate(isolated ? null : g.id)}
              aria-pressed={isolated}
              title={t('planogram.isolateHint')}
              className={cn(
                'flex items-center gap-1.5 rounded-md border px-2 py-1 transition-colors',
                isolated ? 'border-primary bg-primary/10' : 'border-border hover:bg-accent',
              )}
            >
              <span className="inline-block size-3 rounded-[2px]" style={{ background: swatch.bg }} />
              <span>{g.name}</span>
              <span className="tabular-nums text-muted-foreground">{g.compartments}</span>
              {i >= 3 && !isolated && <span className="text-muted-foreground">({t('planogram.other')})</span>}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">{t('planogram.legendGroups')}</p>
    </div>
  );
}
